import {
  useGetCurrentUserQuery,
  useRestoreDemoMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  MoreVerticalOutlineIcon,
  OpenInNew1OutlineIcon,
  PlayOutlineIcon,
  RightOutlineIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  type DemoOverlay,
  PermissionAction,
  PermissionKey,
  type Role,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  Separator,
  SubMenu,
  Tooltip,
  Typography,
  notify,
} from '@carboncopyco/ui';
import { isDesktop, useDemoAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactNode, isValidElement, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsDemoLocked } from '../../../../../hooks';
import {
  ArchiveDemoModal,
  CopyDemoModalWithFolders,
  DuplicateDemoModal,
  MoveDemoModal,
} from '../../../../AssetActions/Demo';
import { AssetLockedModal } from '../../../../AssetLockedModal';
import { DemoLinkModal } from '../../../../DemoLinkModal';
import { GiveADemoModal } from '../../../../PresentationModals';
import { useDesktopDeeplink, usePermissionsContext } from '../../../../utils';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { ArchiveTileDropdown } from '../ArchiveTileDropdown';

export const OverlayActionsDropdown = ({
  demo,
  archived,
  inFolder,
  handleMenuOpen,
  triggerElement,
}: {
  demo: DemoOverlay;
  archived?: boolean;
  inFolder?: boolean;
  handleMenuOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
}) => {
  const flags = useFlags();
  const navigate = useNavigate();
  const [isMenuOpen, { toggle: toggleMenu }] = useBoolean(false);
  const {
    organizationId,
    goToEditor,
    goToDemoPreview,
    goToGiveADemoPreview,
    workspaceId,
    previewArchivedDemo,
    rootPath,
    handleCreatePresentation,
    folderId,
    playPresentation,
    desktopDomain,
  } = useWorkspacePageProps();
  const { id, firstUrl, recordingType } = demo;
  const [restoreDemo] = useRestoreDemoMutation();
  const {
    trackDemoClickPreview,
    trackDemoClickEdit,
    trackDemoClickDuplicate,
    trackDemoClickArchive,
    trackDemoClickCopyToWorkspace,
    trackDemoClickPresent,
    trackDemoMultiUserLock,
  } = useDemoAnalytics(inFolder ? 'folder_page' : 'workspace_page');

  const {
    data: {
      user: { roles } = {},
    } = {},
  } = useGetCurrentUserQuery();
  const userRole: Role = roles?.[organizationId];

  const { getPermissions } = usePermissionsContext();

  const deleteDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.deleteDemoById
  );
  const editDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );
  const duplicateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.duplicateDemo
  );
  const copyToWorkspacePermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.copyDemo
  );
  const moveDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.moveDemo
  );

  const [
    showGiveADemoModal,
    { setTrue: setShowGiveADemoModal, setFalse: setHideGiveADemoModal },
  ] = useBoolean();
  const [
    showDuplicateDemoModal,
    {
      setTrue: setTrueShowDuplicateDemoModal,
      setFalse: setFalseShowDuplicateDemoModal,
    },
  ] = useBoolean(false);
  const [showDemoLinkModal, setShowDemoLinkModal] = useState(false);
  const shareExternallyRef = useRef<HTMLDivElement>();
  const [
    showArchiveDemoModal,
    {
      setTrue: setTrueShowArchiveDemoModal,
      setFalse: setFalseShowArchiveDemoModal,
    },
  ] = useBoolean(false);
  const { exeDeeplinkWithAction, shouldShowDeeplink } = useDesktopDeeplink();

  const [isDemoLockedModal, setIsDemoLockedModal] = useState(false);
  const { data: userData } = useGetCurrentUserQuery();

  const { checkIsDemoLocked, userName } = useIsDemoLocked({
    demoId: String(demo?.id),
    organizationId,
    currentUserEmail: userData.user.email,
    shouldRunInterval: false,
  });

  const handleEditDemo = () => {
    trackDemoClickEdit(demo);
    exeDeeplinkWithAction(
      {
        action: 'edit',
        assetId: id.toString(),
        organizationId: organizationId,
        workspaceId: workspaceId,
        assetType: 'live_demo',
      },
      () => goToEditor(id, recordingType)
    );
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {archived ? (
        <ArchiveTileDropdown
          inFolder={inFolder}
          preview={() => {
            toggleMenu();
            previewArchivedDemo(demo);
          }}
          restore={() => {
            restoreDemo({
              id: String(demo.id),
              organizationId,
            })
              .unwrap()
              .then(() => {
                notify.success(
                  `Overlay "${demo.demoName}" was successfully restored`
                );
              })
              .catch(() => {
                notify.error(`Error restoring demo "${demo.demoName}"`);
              });
          }}
          open={isMenuOpen}
          toggleOpen={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
        />
      ) : (
        <Dropdown
          width={200}
          asChild
          side="right"
          open={isMenuOpen}
          onOpenChange={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
          triggerElement={
            isValidElement(triggerElement) ? (
              triggerElement
            ) : (
              <Button
                aria-label="clone tile actions"
                small
                dark
                icon={<MoreVerticalOutlineIcon />}
              />
            )
          }
        >
          <DropdownItem
            iconLeft={<ShowOutlineIcon size={18} />}
            label="Preview"
            onClick={(event) => {
              event.preventDefault();
              toggleMenu();
              trackDemoClickPreview(demo);
              goToDemoPreview({ id, firstUrl });
            }}
          />

          <DropdownItem
            iconLeft={<PlayOutlineIcon />}
            label="Give a demo"
            onClick={() => {
              trackDemoClickPresent({ ...demo });
              if (isDesktop()) {
                handleCreatePresentation?.(demo);
                return;
              }
              setShowGiveADemoModal();
            }}
          />

          {!demo.readOnly && editDemoPermission && (
            <DropdownItem
              iconLeft={<EditIcon size={18} />}
              label="Edit"
              iconRight={
                shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
              }
              onClick={async (event) => {
                event.preventDefault();
                toggleMenu();

                if (flags.editorMultiUserLocking) {
                  const lockedBy = await checkIsDemoLocked();
                  if (lockedBy) {
                    setIsDemoLockedModal(true);

                    trackDemoMultiUserLock({
                      variables: {
                        userEditing: lockedBy,
                        userTryingToEdit: userData.user.email,
                      },
                      demoId: demo.id,
                    });

                    return;
                  }
                }

                handleEditDemo();
              }}
            />
          )}
          {!demo.readOnly && duplicateDemoPermission && (
            <DropdownItem
              iconLeft={<DuplicateOutlineIcon size={18} />}
              label="Duplicate"
              onClick={() => {
                trackDemoClickDuplicate(demo);
                setTrueShowDuplicateDemoModal();
              }}
            />
          )}
          {!demo.readOnly && moveDemoPermission && (
            <DropdownItem
              iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
              label="Move to..."
              onClick={() => {
                NiceModal.show(MoveDemoModal, {
                  organizationId,
                  demo,
                  workspaceId,
                });
              }}
            />
          )}
          {!demo.readOnly && copyToWorkspacePermission && (
            <DropdownItem
              iconLeft={<CopyOutlineIcon size={18} />}
              label="Copy to..."
              onClick={() => {
                trackDemoClickCopyToWorkspace(demo);
                NiceModal.show(CopyDemoModalWithFolders, {
                  organizationId,
                  demo,
                });
              }}
            />
          )}
          {!demo.readOnly && (
            <>
              <Separator horizontal />
              <SubMenu
                width={205}
                triggerProps={{
                  label: 'Share',
                  iconRight: <RightOutlineIcon />,
                  iconLeft: <ShareOutlineIcon size={18} />,
                }}
              >
                <DropdownItem
                  label="Share internally"
                  onClick={() => {
                    setShowDemoLinkModal(true);
                  }}
                />
                <div ref={shareExternallyRef}>
                  <DropdownItem
                    disabled
                    label="Share externally"
                  />
                </div>
                <Tooltip
                  noArrow
                  placement="right"
                  anchorRef={shareExternallyRef}
                >
                  <div className="p-3">
                    <Typography
                      fontSize="text-ds-200"
                      lineHeight="leading-ds-200"
                      color="text-ds-natural"
                      fontWeight="font-ds-normal"
                      letterSpacing="tracking-ds-tight"
                      label="Live demos can only be shared internally"
                    />
                  </div>
                </Tooltip>
              </SubMenu>

              <Separator horizontal />
            </>
          )}
          {!demo.readOnly && deleteDemoPermission && (
            <DropdownItem
              iconLeft={<ArchiveIcon size={18} />}
              label="Archive"
              onClick={() => {
                trackDemoClickArchive(demo);
                setTrueShowArchiveDemoModal();
              }}
            />
          )}
        </Dropdown>
      )}
      {showGiveADemoModal && (
        <GiveADemoModal
          workspaceId={workspaceId}
          onClose={setHideGiveADemoModal}
          open={showGiveADemoModal}
          demoId={String(demo.id)}
          organizationId={organizationId}
          goToDemoPreview={goToGiveADemoPreview}
          goToPresentationPage={playPresentation}
          goToDemoPresentations={() => {
            navigate(`${rootPath}/demo/${demo.id}/presentations`);
          }}
          desktopDomain={desktopDomain}
        />
      )}
      {showDuplicateDemoModal && (
        <DuplicateDemoModal
          onClose={setFalseShowDuplicateDemoModal}
          organizationId={organizationId}
          demoId={String(id)}
          show={showDuplicateDemoModal}
          folderId={String(demo.folderId)}
        />
      )}
      {showArchiveDemoModal && (
        <ArchiveDemoModal
          folderId={folderId}
          onClose={setFalseShowArchiveDemoModal}
          demoId={String(id)}
          show={showArchiveDemoModal}
          organizationId={organizationId}
        />
      )}
      {showDemoLinkModal && (
        <DemoLinkModal
          workspaceId={workspaceId}
          assetId={String(demo.id)}
          assetType="demo"
          organizationId={organizationId}
          isOpen={showDemoLinkModal}
          onClose={() => {
            setShowDemoLinkModal(false);
          }}
          role={userRole}
        />
      )}
      {isDemoLockedModal && (
        <AssetLockedModal
          userName={userName}
          buttonLabel="Okay"
          onButtonClick={() => {
            setIsDemoLockedModal(false);
          }}
        />
      )}
    </div>
  );
};
